import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useIonModal } from '@ionic/react'
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces'
import moment from 'moment-timezone'
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useMyMonitoringActivateControl } from '@apps-common/apis/my-monitoring/control'
import { useActivateUnitControl } from '@apps-common/apis/portal/control'
import { ControlsPendingModal } from '@apps-common/components/controls'
import {
  CONTROL_TYPE_ACCESS_CONTROL,
  CONTROL_TYPE_ALARM,
  ControlMessages,
  isControlStateMatch
} from '@apps-common/config/controls'
import { PushUnitControl } from '@apps-common/config/push-notification'
import { useNotifications } from '@apps-common/contexts/NotificationContext'
import { ENV_APP_PROJECT } from '@apps-common/lib/environment'
import { invalidateSiteEventQueries } from '@apps-common/lib/invalidateSiteEventQueries'
import { modalRightSlideInAnimation, modalRightSlideOutAnimation } from '@apps-common/lib/modal-animations'
import { toastError, toastSuccess, toastWarning } from '@apps-common/lib/toast'
import { useMyMonitoringNotificationsStore } from '@apps-common/stores'
import { INotificationPayload } from '@apps-common/types/portal/push-registration'
import { IActivateControlResponseModel, IUnitControlWithStateModel } from '@apps-common/types/portal/unit-control'

type AwaitingStatusType = 'sending' | 'awaiting-update'
export type ControlRefreshOptions = { onRefreshed?: (control: IUnitControlWithStateModel) => void }

const project = ENV_APP_PROJECT
export interface IListeningControl {
  sourceId: string
  listenerId: string
  control: IUnitControlWithStateModel
  callback: (control: IUnitControlWithStateModel) => void
  refresh?: (options?: ControlRefreshOptions) => void
}

export interface IAwaitingControl {
  control: IUnitControlWithStateModel
  desiredState: string
  status: AwaitingStatusType
  timestamp: string
}

const buildControlStateSignature = (control: Pick<IUnitControlWithStateModel, 'currentState' | 'controlStates'>) =>
  `${control.currentState || ''}|${control.controlStates || ''}`

const buildControlAlreadyInStateMessage = (controlName: string | undefined, desiredState: string) =>
  `${controlName || 'Control'} already in ${desiredState.toLowerCase()} state.`

const shouldAwaitControlUpdate = (control: IUnitControlWithStateModel) =>
  control.requiresControlState || control.controlTypeId === CONTROL_TYPE_ALARM || control.controlTypeId === CONTROL_TYPE_ACCESS_CONTROL

const buildControlRequestSentMessage = (message?: string, waitingForUpdate = true) => {
  if (!waitingForUpdate) {
    return message || 'Command sent.'
  }

  if (project === 'my-monitoring') {
    return 'Request sent, waiting for update.'
  }

  return message || 'Commands sent, waiting for update.'
}

const buildControlRequestFailedMessage = (message?: string) => {
  if (project === 'my-monitoring') {
    return 'Failed to send request.'
  }

  return message || 'Failed to send commands, please try again.'
}

export type IControlContext = {
  awaiting: IAwaitingControl[]
  activate: (control: IUnitControlWithStateModel, desiredState: string) => void
  getAwaiting: (controlId: number) => IAwaitingControl | undefined
  subscribe: (listener: IListeningControl) => void
  unsubscribe: (listener: IListeningControl) => void
}

export const ControlContext = createContext<IControlContext>({
  awaiting: [],
  activate: () => undefined,
  getAwaiting: () => undefined,
  subscribe: (listener: IListeningControl) => undefined,
  unsubscribe: (listener: IListeningControl) => undefined
})

export const ControlContextBridge = ({ value, children }: { value: IControlContext; children: ReactNode }) => (
  <ControlContext.Provider value={value}>{children}</ControlContext.Provider>
)

export const ControlProvider = ({ children }: any) => {
  const notifications = useNotifications()
  const { notificationsEnabled } = useMyMonitoringNotificationsStore()
  const shouldPollAwaitingControls = project === 'my-monitoring' && notificationsEnabled !== true
  const shouldPollAwaitingControlsRef = useRef(shouldPollAwaitingControls)

  const [timerId, setTimerId] = useState<number | undefined>()
  const [listeners, setListeners] = useState<IListeningControl[]>([])
  const listenersRef = useRef(listeners)
  const subscribedControlIdsRef = useRef<Set<number>>(new Set())
  const observedControlStateRef = useRef<Map<number, string>>(new Map())
  const lastAwaitingPollRefreshRef = useRef<string | undefined>(undefined)

  const [awaiting, setAwaiting] = useState<IAwaitingControl[]>([])
  const awaitingRef = useRef(awaiting)
  const setAwaitingState = useCallback((updater: (prev: IAwaitingControl[]) => IAwaitingControl[]) => {
    setAwaiting((prev) => {
      const next = updater(prev)
      awaitingRef.current = next
      return next
    })
  }, [])

  const clearAwaitingControl = useCallback(
    (controlId: number) => {
      setAwaitingState((prev) => {
        const hasAwaiting = prev.some((a) => a.control.unitControlId === controlId)

        return hasAwaiting ? prev.filter((a) => a.control.unitControlId !== controlId) : prev
      })
    },
    [setAwaitingState]
  )

  const recordControlStateObservation = useCallback(
    (control: IUnitControlWithStateModel) => {
      const controlId = control.unitControlId
      const previousSignature = observedControlStateRef.current.get(controlId)
      const nextSignature = buildControlStateSignature(control)

      if (previousSignature && previousSignature !== nextSignature) {
        clearAwaitingControl(controlId)
      }

      observedControlStateRef.current.set(controlId, nextSignature)
    },
    [clearAwaitingControl]
  )

  const resolveAwaitingControlRefresh = useCallback(
    (control: IUnitControlWithStateModel, notifyUnchanged: boolean) => {
      const controlId = control.unitControlId
      const qItem = awaitingRef.current.find((a) => a.control.unitControlId === controlId)
      const nextSignature = buildControlStateSignature(control)

      observedControlStateRef.current.set(controlId, nextSignature)

      if (!qItem) {
        return
      }

      const awaitingSignature = buildControlStateSignature(qItem.control)
      const isDesiredState = control.currentState && isControlStateMatch(control.currentState, qItem.desiredState)
      const stateChanged = nextSignature !== awaitingSignature

      if (!isDesiredState && !stateChanged && notifyUnchanged) {
        toastWarning(ControlMessages.AwaitingTimeout)
      }

      if (isDesiredState || stateChanged || notifyUnchanged) {
        clearAwaitingControl(controlId)
      }

      if (isDesiredState || stateChanged) {
        invalidateSiteEventQueries(control.unitId)
      }
    },
    [clearAwaitingControl]
  )

  const useActivateControlHook = useMemo(() => {
    if (project === 'my-monitoring') {
      return useMyMonitoringActivateControl
    }

    return useActivateUnitControl
  }, [])

  const broadcastToListeners = (id: number, controlState: string, controlStates: string) => {
    const matchedListeners = listenersRef.current.filter((l) => l.control.unitControlId === id)
    const observedControl = matchedListeners[0]?.control

    if (observedControl) {
      observedControlStateRef.current.set(
        id,
        buildControlStateSignature({
          ...observedControl,
          currentState: controlState,
          controlStates: controlStates || observedControl.controlStates
        })
      )

      invalidateSiteEventQueries(observedControl.unitId)
    }

    if (matchedListeners?.length > 0) {
      for (let i = 0; i < matchedListeners.length; i += 1) {
        const listener = matchedListeners[i]

        listener.callback({
          ...listener.control,
          currentState: controlState,
          controlStates: controlStates || listener.control.controlStates
        })
      }
    }
  }

  const broadcastToListenersRefresh = (id: number, options?: ControlRefreshOptions) => {
    const matchedListeners = listenersRef.current.filter((l) => l.control.unitControlId === id)

    if (matchedListeners?.length > 0) {
      for (let i = 0; i < matchedListeners.length; i += 1) {
        const listener = matchedListeners[i]

        if (listener && listener.refresh) {
          listener.refresh(options)
        }
      }
    }

    return matchedListeners?.length > 0
  }

  const refreshAwaitingControls = (notifyUnchanged: boolean) => {
    const awaitingControlIds = awaitingRef.current.map((qItem) => qItem.control.unitControlId)

    const uniqueIds = Array.from(new Set(awaitingControlIds))

    uniqueIds.forEach((controlId) =>
      broadcastToListenersRefresh(controlId, {
        onRefreshed: (control) => resolveAwaitingControlRefresh(control, notifyUnchanged)
      })
    )
  }

  const processControlActivationResult = (id: number, result: boolean, data?: IActivateControlResponseModel['data']) => {
    setAwaitingState((prev) => {
      const index = prev.findIndex((a) => a.control.unitControlId === id)
      const qItem = index !== -1 ? prev[index] : null

      if (qItem && data) {
        broadcastToListeners(id, data?.currentState, data?.controlStates || qItem.control.controlStates)
      }

      if (result) {
        if (!qItem) {
          return prev
        }

        if (!shouldAwaitControlUpdate(qItem.control)) {
          return prev.filter((a) => a.control.unitControlId !== id)
        }

        if (data?.currentState && isControlStateMatch(data.currentState, qItem.desiredState)) {
          broadcastToListenersRefresh(id, {
            onRefreshed: (control) => resolveAwaitingControlRefresh(control, false)
          })

          return prev.filter((a) => a.control.unitControlId !== id)
        }

        return [...prev.filter((a) => a.control.unitControlId !== id), { ...qItem, status: 'awaiting-update' }]
      }

      if (index !== -1) {
        return prev.filter((a) => a.control.unitControlId !== id)
      }

      return prev
    })
  }

  const { mutate: activateControl } = useActivateControlHook(
    // notify
    false,
    // onSuccess
    (res, props) => {
      const qItem = awaitingRef.current.find((a) => a.control.unitControlId === props.controlId)
      const waitingForUpdate = qItem ? shouldAwaitControlUpdate(qItem.control) : true
      const alreadyInDesiredStateMessage =
        qItem && res?.data?.currentState && isControlStateMatch(res.data.currentState, qItem.desiredState)
          ? buildControlAlreadyInStateMessage(qItem.control.controlName, qItem.desiredState)
          : undefined

      processControlActivationResult(props.controlId, true, res?.data)

      toastSuccess(alreadyInDesiredStateMessage || buildControlRequestSentMessage(res?.message, waitingForUpdate))
    },
    // onFailure
    (variables) => {
      processControlActivationResult(variables.model.controlId, false)
      toastError(buildControlRequestFailedMessage())
    }
  )

  useEffect(() => {
    listenersRef.current = listeners
  }, [listeners])

  useEffect(() => {
    awaitingRef.current = awaiting
  }, [awaiting])

  useEffect(() => {
    shouldPollAwaitingControlsRef.current = shouldPollAwaitingControls
  }, [shouldPollAwaitingControls])

  const onNotificationReceived = (event: INotificationPayload) => {
    const { data } = event
    const { controlId, controlState, controlStates } = data || {}

    if (controlId && controlState) {
      const controlIdInt = parseInt(controlId, 10)
      const hasAwaitingBefore = awaitingRef.current.some((a) => a.control.unitControlId === controlIdInt)
      const isSubscribed = subscribedControlIdsRef.current.has(controlIdInt)

      console.log('AWAITING: ', awaitingRef.current)
      console.log('SUBSCRIBED: ', subscribedControlIdsRef.current)

      if (!hasAwaitingBefore && !isSubscribed) {
        return
      }

      if (hasAwaitingBefore) {
        console.log('[notif-update] control update', controlIdInt)
      }

      clearAwaitingControl(controlIdInt)

      broadcastToListeners(controlIdInt, controlState, controlStates)
    }
  }

  const processQueue = async () => {
    const removalIds: number[] = []
    const shouldRefreshAwaitingPoll =
      shouldPollAwaitingControlsRef.current &&
      (!lastAwaitingPollRefreshRef.current || moment(lastAwaitingPollRefreshRef.current).add(30, 'second').isBefore(moment()))

    for (let i = 0; i < awaitingRef.current.length; i += 1) {
      const qItem = awaitingRef.current[i]

      if (qItem.status === 'awaiting-update' && shouldRefreshAwaitingPoll) {
        broadcastToListenersRefresh(qItem.control.unitControlId, {
          onRefreshed: (control) => resolveAwaitingControlRefresh(control, false)
        })
      }

      if (qItem.status === 'awaiting-update' && moment(qItem.timestamp).add(180, 'second').isBefore(moment())) {
        // check if control is refreshed
        const refreshed = broadcastToListenersRefresh(qItem.control.unitControlId, {
          onRefreshed: (control) => resolveAwaitingControlRefresh(control, true)
        })

        if (!refreshed) {
          removalIds.push(qItem.control.unitControlId)
        }
      }
    }

    if (shouldRefreshAwaitingPoll) {
      lastAwaitingPollRefreshRef.current = moment().toISOString()
    }

    if (removalIds.length > 0) {
      setAwaitingState((prev) => [...prev.filter((a) => !removalIds.includes(a.control.unitControlId))])
    }
  }

  const timerTick = () => {
    if (awaitingRef.current.length > 0 && awaitingRef.current.some((a) => a.status === 'awaiting-update')) {
      processQueue()
    }
  }

  const startTimer = () => {
    const id = window.setInterval(() => timerTick(), 5000)

    setTimerId(id)
  }

  useEffect(() => {
    if (awaiting.some((a) => a.status === 'awaiting-update') && !timerId) {
      startTimer()
    } else if (!awaiting.some((a) => a.status === 'awaiting-update') && timerId) {
      window.clearInterval(timerId)
      setTimerId(undefined)
    }
  }, [awaiting, timerId])

  useEffect(
    () => () => {
      if (timerId) {
        window.clearInterval(timerId)
      }
    },
    [timerId]
  )

  useEffect(() => {
    const refreshOnResume = () => {
      refreshAwaitingControls(false)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshOnResume()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    const removeNativeListeners: Array<() => void> = []
    let disposed = false

    if (Capacitor.isNativePlatform()) {
      App.addListener('resume', refreshOnResume).then((handle) => {
        if (disposed) {
          handle.remove()

          return
        }

        removeNativeListeners.push(() => handle.remove())
      })

      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          refreshOnResume()
        }
      }).then((handle) => {
        if (disposed) {
          handle.remove()

          return
        }

        removeNativeListeners.push(() => handle.remove())
      })
    }

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      removeNativeListeners.forEach((removeNativeListener) => removeNativeListener())
    }
  }, [])

  useEffect(() => {
    if (notifications) {
      notifications.subscribeToNotifications({ id: 'control-context', destination: PushUnitControl.destination, onNotificationReceived })
    }
    return () => {
      notifications?.unsubscribeToNotifications('control-context')
    }
  }, [notifications])

  const [present, dismiss] = useIonModal(ControlsPendingModal, {
    componentProps: {},
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
    // enterAnimation: modalRightSlideInAnimation,
    // leaveAnimation: modalRightSlideOutAnimation
  })

  const openModal = () => {
    present({
      enterAnimation: modalRightSlideInAnimation,
      leaveAnimation: modalRightSlideOutAnimation,
      backdropDismiss: true,
      onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
        // close
      },
      onDidDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
        // close
      }
    })
  }

  const activate = useCallback(
    (control: IUnitControlWithStateModel, desiredState: string) => {
      const exists = awaitingRef.current.some((a) => a.control.unitControlId === control.unitControlId)

      if (exists) {
        return
      }

      const newActivation: IAwaitingControl = { control, desiredState, status: 'sending', timestamp: moment().toISOString() }

      setAwaitingState((prev) => [...prev, newActivation])

      activateControl({
        model: {
          controlId: newActivation.control.unitControlId,
          controlState: newActivation.control.currentState,
          controlAction: desiredState,
          onTime: 0
        }
      })
    },
    [activateControl]
  )

  const getAwaiting = useCallback((controlId: number) => awaitingRef.current.find((a) => a?.control?.unitControlId === controlId), [])

  const subscribe = useCallback(
    (listener: IListeningControl) => {
      recordControlStateObservation(listener.control)

      setListeners((prevState) => {
        const index = prevState.findIndex((l) => l.listenerId === listener.listenerId)

        let nextState = prevState

        if (index === -1) {
          nextState = [...prevState, listener]
        } else {
          const cloned = [...prevState]
          cloned[index] = listener
          nextState = cloned
        }

        subscribedControlIdsRef.current = new Set(nextState.map((item) => item.control.unitControlId))
        return nextState
      })
    },
    [recordControlStateObservation]
  )

  const unsubscribe = useCallback((listener: IListeningControl) => {
    setListeners((prevState) => {
      const nextState = prevState.filter((l) => l.listenerId !== listener.listenerId)
      subscribedControlIdsRef.current = new Set(nextState.map((item) => item.control.unitControlId))
      return nextState
    })
  }, [])

  const value = useMemo(
    () => ({
      awaiting,
      activate,
      getAwaiting,
      subscribe,
      unsubscribe
    }),
    [activate, awaiting, getAwaiting, subscribe, unsubscribe]
  )

  return <ControlContext.Provider value={value}>{children}</ControlContext.Provider>
}

export const useControlsContext = () => useContext(ControlContext)
